import { useIonViewDidLeave, useIonViewWillEnter } from '@ionic/react';
import React, { FC, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { appBarActions } from 'src/redux/appBar/actions';
import { useParsedLog } from 'src/redux/log/thunk/fetchLog';
import { AppState } from 'src/redux/types';
import { useLimitedMode } from 'src/utils/auth/useLimitedMode';
import { useSocketGet } from 'src/utils/socket';
import { SocketContextPortal } from 'src/utils/socket/context/portalSocketContext';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { AnnotationsContextProvider } from '../../components/annotations/AnnotationsContext';
import { LogPrintConfig } from '../../models/jobLog';
import {
  canSeeAnnotations,
  canSeeIdleVsPressure,
  canSeePieCharts,
  canSeePressures,
  canSeeTimestamps,
  canSeeTubeGraph,
  canShowAnnotationUser,
  usePolicy,
} from '../../models/policies';
import { LogInfoPrint } from './LogInfoPrint';
import { useLogCreatedInfo } from './util';
import { useGA4 } from '../../utils/ga4';

interface LogPrintQueryParams {
  timestamps: boolean;
  pressure: boolean;
  pieCharts: boolean;
  idle: boolean;
  tubesCleaned: boolean;
  tubesCompletion: boolean;
  tubesAutostrokes: boolean;
  tubesDepthCleaned: boolean;
  tubesLanceSpeed: boolean;
  tubesTime: boolean;
  annotations: boolean;
  startTime?: number;
  endTime?: number;
}

/** A custom hook that builds on useLocation to parse the query string for you. */
function useQuery() {
  const { search } = useLocation<LogPrintQueryParams>();

  const params = React.useMemo(() => {
    const queryParams = new URLSearchParams(search);

    const startTime = Number.parseInt(queryParams.get('startTime') ?? '');
    const endTime = Number.parseInt(queryParams.get('endTime') ?? '');

    const parsedParams: LogPrintQueryParams = {
      annotations: queryParams.has('annotations'),
      idle: queryParams.has('idle'),
      pieCharts: queryParams.has('pieCharts'),
      pressure: queryParams.has('pressure'),
      tubesAutostrokes: queryParams.has('tubesAutostrokes'),
      tubesCleaned: queryParams.has('tubesCleaned'),
      tubesCompletion: queryParams.has('tubesCompletion'),
      tubesDepthCleaned: queryParams.has('tubesDepthCleaned'),
      tubesLanceSpeed: queryParams.has('tubesLanceSpeed'),
      tubesTime: queryParams.has('tubesTime'),
      timestamps: queryParams.has('timestamps'),
      startTime: Number.isNaN(startTime) ? undefined : startTime,
      endTime: Number.isNaN(endTime) ? undefined : endTime,
    };

    return parsedParams;
  }, [search]);

  return params;
}

export const LogPrintPage: FC = () => {
  document.title = 'Printable Log Page'; // Purposely English only for Google Analytics
  useGA4();

  const routeParams = useParams<{ id: string }>();
  const queryParams = useQuery();

  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();

  const { item: logInfo } = useSocketGet(SocketContextSentinel, 'logs', routeParams.id);
  const { item: org } = useSocketGet(SocketContextPortal, 'organizations', logInfo?.org);
  const { isLimited } = useLimitedMode();

  const permTimestamps = usePolicy(canSeeTimestamps, logInfo);
  const permPressure = usePolicy(canSeePressures, logInfo);
  const permPieCharts = usePolicy(canSeePieCharts, logInfo);
  const permIdleVsPressure = usePolicy(canSeeIdleVsPressure, logInfo);
  const permTubesCleaned = usePolicy(canSeeTubeGraph, logInfo);
  const permTubeSheets = usePolicy(canSeeTubeGraph, logInfo);
  const permAnnotations = usePolicy(canSeeAnnotations, logInfo);
  const permAnnotationUsers = usePolicy(canShowAnnotationUser, logInfo);

  const displaySettings: LogPrintConfig = useMemo(
    () => ({
      showTimestamps: permTimestamps && queryParams.timestamps,
      showPressures: permPressure && queryParams.pressure,
      showPieCharts: permPieCharts && queryParams.pieCharts,
      showIdleVsPressure: permIdleVsPressure && queryParams.idle,

      showTubesCleaned: permTubesCleaned && queryParams.tubesCleaned,
      showTubeSheetCompletion: permTubeSheets && queryParams.tubesCompletion,
      showTubeSheetAutostrokes: permTubeSheets && queryParams.tubesAutostrokes,
      showTubeSheetDepthCleaned: permTubeSheets && queryParams.tubesDepthCleaned,
      showTubeSheetLanceSpeed: permTubeSheets && queryParams.tubesLanceSpeed,
      showTubeSheetTime: permTubeSheets && queryParams.tubesTime,

      showAnnotations: permAnnotations && queryParams.annotations,
      showAnnotationUser: permAnnotationUsers && queryParams.annotations,
      showEstimatedTimeToCompletion: !!logInfo?.status && logInfo.status !== 'complete' && queryParams.timestamps,

      startTime: queryParams.startTime,
      endTime: queryParams.endTime,
    }),
    [
      logInfo?.status,
      permAnnotationUsers,
      permAnnotations,
      permIdleVsPressure,
      permPieCharts,
      permPressure,
      permTimestamps,
      permTubeSheets,
      permTubesCleaned,
      queryParams.annotations,
      queryParams.endTime,
      queryParams.idle,
      queryParams.pieCharts,
      queryParams.pressure,
      queryParams.startTime,
      queryParams.timestamps,
      queryParams.tubesAutostrokes,
      queryParams.tubesCleaned,
      queryParams.tubesCompletion,
      queryParams.tubesDepthCleaned,
      queryParams.tubesLanceSpeed,
      queryParams.tubesTime,
    ],
  );

  // Force refresh user visits the page by ID - we always want the latest, if available.
  const { abortCtrl, data } = useParsedLog(logInfo?._id, logInfo?.s3?.ETag);
  const formattedName = useLogCreatedInfo(logInfo);

  useIonViewWillEnter(() => {
    dispatch(appBarActions.showBack(true));
  }, [dispatch]);

  useIonViewDidLeave(() => {
    abortCtrl.current?.abort();
  }, []);

  const loadProgress = React.useMemo(() => {
    if (data?.totalLines === undefined) return 0;
    if (data.parsedLines === data.totalLines) return 1;
    return Math.max(0.1, data.parsedLines / data.totalLines);
  }, [data?.parsedLines, data?.totalLines]);

  const isLimitedMode = React.useMemo(() => {
    return !logInfo?._id || isLimited;
  }, [isLimited, logInfo?._id]);

  return (
    <AnnotationsContextProvider parentId={routeParams.id}>
      <LogInfoPrint
        info={logInfo ?? undefined}
        data={data?.data}
        description={logInfo?.description || logInfo?.name || logInfo?.originalFileName}
        subheading={formattedName}
        orgName={org?.orgName}
        fetchProgress={loadProgress}
        status={logInfo?.status ?? 'complete'}
        hasErrors={!!data?.hasParseErrors}
        printConfig={displaySettings}
        isLimitedMode={isLimitedMode}
        timezone={logInfo?.deviceTimezone || 'UTC'}
      />
    </AnnotationsContextProvider>
  );
};
